import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { IUserRepairWorks } from '../models/UserInfo';
import { userInfoApi } from '../services/apiServices/user/userInfo';
import { getRepairProgressPercent } from '../utils';

export const useRepairWorkProgress = (work: IUserRepairWorks) => {
  const [progressPercent, setProgressPercent] = useState<number | null>(() =>
    work.fromTime === null ? work.fromTime : getRepairProgressPercent(work),
  );
  const isFinished = useRef(false);
  const [getRepairWorks] = userInfoApi.useLazyGetUserRepairWorksQuery();

  useEffect(() => {
    if (work.fromTime === null) {
      setProgressPercent(null);
      return undefined;
    }
    const start = DateTime.fromFormat(`${work.fromDate} ${work.fromTime}`, 'yyyy-MM-dd HH:mm', { zone: 'Europe/Kyiv' });
    const end = DateTime.fromFormat(`${work.toDate} ${work.toTime}`, 'yyyy-MM-dd HH:mm', { zone: 'Europe/Kyiv' });
    isFinished.current = false;

    const totalMs = end.toMillis() - start.toMillis();

    const handleFinished = () => {
      if (isFinished.current) return;
      isFinished.current = true;
      let attempts = 0;
      const intervalId = setInterval(async () => {
        try {
          attempts += 1;
          const result = await getRepairWorks(undefined, false).unwrap();
          const actualWork = result?.find((item) => item.id === work.id);
          const workPeriodChanged =
            actualWork &&
            (work.fromDate !== actualWork.fromDate ||
              work.fromTime !== actualWork.fromTime ||
              work.toDate !== actualWork.toDate ||
              work.toTime !== actualWork.toTime);
          if (!actualWork || workPeriodChanged || attempts >= 5) {
            clearInterval(intervalId);
          }
        } catch {
          clearInterval(intervalId);
        }
      }, 3000);
    };

    if (totalMs <= 0) {
      setProgressPercent(100);
      handleFinished();
      return undefined;
    }
    const currentPercent = getRepairProgressPercent(work);
    setProgressPercent(currentPercent);
    if (currentPercent >= 100) {
      setProgressPercent(100);
      handleFinished();
      return undefined;
    }
    const updateInterval = Math.max(totalMs / 100, 1000);

    const intervalId = setInterval(() => {
      const nextPercent = getRepairProgressPercent(work);
      setProgressPercent(nextPercent);
      if (nextPercent >= 100) {
        clearInterval(intervalId);
        handleFinished();
      }
    }, updateInterval);

    return () => clearInterval(intervalId);
  }, [work.id, work.fromDate, work.fromTime, work.toDate, work.toTime]);

  return progressPercent;
};
